/**
 * Fantasy Console 0 - Sign In Modal
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { getAuthErrorMessage } from '../../utils/authErrors'
import styles from './AuthModal.module.css'

interface SignInModalProps {
  isOpen: boolean
  onClose: () => void
  onSwitchToSignUp: () => void
}

export function SignInModal({
  isOpen,
  onClose,
  onSwitchToSignUp,
}: SignInModalProps) {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setPassword('')
      setError(null)
      setTimeout(() => emailInputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setIsLoading(true)

      try {
        await signIn(email.trim(), password)
        onClose()
      } catch (err) {
        setError(getAuthErrorMessage(err))
      } finally {
        setIsLoading(false)
      }
    },
    [email, password, signIn, onClose],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  return (
    <div className='modal-overlay' onClick={onClose} onKeyDown={handleKeyDown}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2 className='modal-title'>Sign In</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='modal-body'>
            {error && <div className={styles.error}>{error}</div>}

            <div className='form-group'>
              <label className='form-label' htmlFor='signin-email'>
                Email
              </label>
              <input
                ref={emailInputRef}
                id='signin-email'
                type='email'
                className='form-input'
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                placeholder='you@example.com'
                autoComplete='email'
                required
              />
            </div>

            <div className='form-group'>
              <label className='form-label' htmlFor='signin-password'>
                Password
              </label>
              <input
                id='signin-password'
                type='password'
                className='form-input'
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(null)
                }}
                placeholder='Your password'
                autoComplete='current-password'
                required
              />
            </div>

            <div className={styles.switchPrompt}>
              Don't have an account?{' '}
              <button
                type='button'
                className={styles.switchLink}
                onClick={onSwitchToSignUp}
              >
                Sign up
              </button>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-ghost' onClick={onClose}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
